// @flow
import type { FSA } from 'types/redux';
import type {
  CohortDriver,
  Location,
  Behaviour,
} from 'types/entities';
import type { Booking } from 'types/bookings';
import ga from 'react-ga';

import { API_REQUEST } from 'middlewares/api-middleware';
import { DRIVERS, DRIVERS_ACTIONS } from 'utils/ga-utils';

export const DRIVER_LOGIN: string = 'DRIVER_LOGIN';
export function driverLoginInstant(cohortID: string, driverID: string,
  behaviour: Behaviour): FSA {
  return {
    type: DRIVER_LOGIN,
    payload: {
      cohortID,
      driverID,
      behaviour,
    },
  };
}

export function driverLogin(cohortID: string, driverID: string,
  driver: CohortDriver, location: Location): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_LOGIN,
  });
  return {
    type: DRIVER_LOGIN,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/login`,
      data: {
        behaviour: driver.behaviour,
        longitude: location[0],
        latitude: location[1],
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
    },
  };
}

export const DRIVER_LOGOUT: string = 'DRIVER_LOGOUT';
export function driverLogoutInstant(cohortID: string, driverID: string): FSA {
  return {
    type: DRIVER_LOGOUT,
    payload: {
      cohortID,
      driverID,
    },
  };
}

export function driverLogout(cohortID: string, driverID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_LOGOUT,
  });
  return {
    type: DRIVER_LOGOUT,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/logout`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
    },
  };
}

export const DRIVER_DELETE: string = 'DRIVER_DELETE';
export function driverDeleteInstant(cohortID: string, driverID: string): FSA {
  return {
    type: DRIVER_DELETE,
    payload: {
      cohortID,
      driverID,
    },
  };
}

export function driverDelete(cohortID: string, driverID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_DELETE,
  });
  return {
    type: DRIVER_DELETE,
    payload: {
      method: 'DELETE',
      url: `/drivers/${driverID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
    },
  };
}

export const DRIVER_SET_AVAILABILITY: string = 'DRIVER_SET_AVAILABILITY';
export function driverSetAvailabilityInstant(cohortID: string, driverID: string,
  availability: boolean): FSA {
  return {
    type: DRIVER_SET_AVAILABILITY,
    payload: {
      availability,
      cohortID,
      driverID,
    },
  };
}

export function driverSetAvailability(cohortID: string, driverID: string,
  availability: boolean): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_SET_AVAILABILITY,
  });
  return {
    type: DRIVER_SET_AVAILABILITY,
    payload: {
      method: 'PUT',
      url: `/drivers/${driverID}/availability`,
      data: {
        availability,
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
      availability,
    },
  };
}

export const DRIVER_SET_AUTO_ACCEPT: string = 'DRIVER_SET_AUTO_ACCEPT';
export function driverSetAutoAcceptInstant(cohortID: string, driverID: string,
  isTurnedOn: boolean): FSA {
  return {
    type: DRIVER_SET_AUTO_ACCEPT,
    payload: {
      isTurnedOn,
      cohortID,
      driverID,
    },
  };
}

export function driverSetAutoAccept(cohortID: string, driverID: string,
  isTurnedOn: boolean): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_SET_AUTO_ACCEPT,
  });
  return {
    type: DRIVER_SET_AUTO_ACCEPT,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/auto-accept`,
      data: {
        isTurnedOn,
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
      isTurnedOn,
    },
  };
}

export const DRIVER_SET_LOCATION: string = 'DRIVER_SET_LOCATION';
export function driverSetLocationInstant(cohortID: string, driverID: string,
  location: Location, selfGenerated: boolean): FSA {
  // selfGenerated flag differentiates the source of the event trigger (websocket / ui)
  return {
    type: DRIVER_SET_LOCATION,
    payload: {
      location,
      cohortID,
      driverID,
      selfGenerated,
    },
  };
}

export function driverSetLocation(cohortID: string, driverID: string,
  location: Location): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_SET_LOCATION,
  });
  return {
    type: DRIVER_SET_LOCATION,
    payload: {
      method: 'PUT',
      url: `/drivers/${driverID}/location`,
      data: {
        longitude: location[0],
        latitude: location[1],
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      driverID,
    },
  };
}

export const DRIVER_BOOKING_BID: string = 'DRIVER_BOOKING_BID';
export function driverBookingBid(driverID: string, bookingID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_BID,
  });
  return {
    type: DRIVER_BOOKING_BID,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/bookings/${bookingID}/bid`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DRIVER_BOOKING_CANCEL: string = 'DRIVER_BOOKING_CANCEL';
export function driverBookingCancel(driverID: string, bookingID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_CANCEL,
  });
  return {
    type: DRIVER_BOOKING_CANCEL,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/bookings/${bookingID}/cancel`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DRIVER_BOOKING_PICK_UP: string = 'DRIVER_BOOKING_PICK_UP';
export function driverBookingPickUp(driverID: string, bookingID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_PICK_UP,
  });
  return {
    type: DRIVER_BOOKING_PICK_UP,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/bookings/${bookingID}/pick_up`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DRIVER_BOOKING_ADDITIONAL_DROP_OFF: string = 'DRIVER_BOOKING_ADDITIONAL_DROP_OFF';
export function driverBookingAdditionalDropOff(driverID: string, bookingID: string, fare: number, bookingFee: number,
  otherFees: number, dropOffNumber: number): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_DROP_OFF,
  });
  return {
    type: DRIVER_BOOKING_ADDITIONAL_DROP_OFF,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/bookings/${bookingID}/drop_off`,
      data: {
        dropOffNumber,
        fare,
        bookingFee,
        otherFees,
      },
    },
    meta: {
      [API_REQUEST]: true,
      driverID,
      bookingID,
    },
  };
}

export const DRIVER_BOOKING_DROP_OFF: string = 'DRIVER_BOOKING_DROP_OFF';
export function driverBookingDropOff(driverID: string, bookingID: string, fare: number,
  bookingFee: number, otherFees: number): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_DROP_OFF,
  });
  return {
    type: DRIVER_BOOKING_DROP_OFF,
    payload: {
      method: 'POST',
      url: `/drivers/${driverID}/bookings/${bookingID}/drop_off`,
      data: {
        fare,
        bookingFee,
        otherFees,
      },
    },
    meta: {
      [API_REQUEST]: true,
      driverID,
      bookingID,
    },
  };
}

export const DRIVER_BOOKING_NEW: string = 'DRIVER_BOOKING_NEW';
export function driverBookingNew(cohortID: string, driverID: string, bookingID: string,
  booking: Booking): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_NEW,
  });
  return {
    type: DRIVER_BOOKING_NEW,
    payload: {
      cohortID,
      driverID,
      bookingID,
      booking,
    },
  };
}

export const DRIVER_BOOKING_STATE_CHANGE = 'DRIVER_BOOKING_STATE_CHANGE';
export function driverBookingStateChange(cohortID: string, driverID: string,
  bookingID: string, state: number): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_STATE_CHANGE,
  });
  return {
    type: DRIVER_BOOKING_STATE_CHANGE,
    payload: {
      cohortID,
      driverID,
      bookingID,
      state,
    },
  };
}

export const DRIVER_BOOKING_DELETE = 'DRIVER_BOOKING_DELETE';
export function driverBookingDelete(cohortID: string, driverID: string,
  bookingID: string): FSA {
  ga.event({
    category: DRIVERS,
    action: DRIVERS_ACTIONS.DRIVER_BOOKING_DELETE,
  });
  return {
    type: DRIVER_BOOKING_DELETE,
    payload: {
      cohortID,
      driverID,
      bookingID,
    },
  };
}

export const DRIVER_BATCH_SET_LOCATION = 'DRIVER_BATCH_SET_LOCATION';
export function batchDriverSetLocation(cohortID: string, driverLocations: { [string]: Location }) {
  return {
    type: DRIVER_BATCH_SET_LOCATION,
    payload: {
      cohortID,
      driverLocations,
    },
  };
}



/** WEBPACK FOOTER **
 ** ./src/js/actions/drivers.js
 **/